import { FC } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  useTheme,
} from '@mui/material';
import {
  Warning,
  Error,
  Info,
  ArrowForward,
} from '@mui/icons-material';
import { useTranslation } from 'src/hooks/useTranslationHook';
import { useNavigate } from 'react-router-dom';
import {
  IFinanceActionItem,
  IPeopleActionItem,
  IEventsActionItem,
} from '../../../../../apis/dashboard/dashboard.interfaces';

interface ActionItemsProps<T extends IFinanceActionItem | IPeopleActionItem | IEventsActionItem> {
  title: string;
  items: T[];
  isLoading?: boolean;
  emptyMessage?: string;
  dataTestId?: string;
}

const getPriorityIcon = (priority: 'high' | 'medium' | 'low') => {
  switch (priority) {
    case 'high':
      return <Error />;
    case 'medium':
      return <Warning />;
    case 'low':
      return <Info />;
    default:
      return <Info />;
  }
};

export const ActionItems: FC<ActionItemsProps<any>> = ({
  title,
  items,
  isLoading = false,
  emptyMessage,
  dataTestId = 'action-items',
}) => {
  const { translateText } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight={200}
        data-testid={`${dataTestId}-loading`}
      >
        <Typography variant="body2" color="textSecondary">
          {translateText('common.loading')}
        </Typography>
      </Box>
    );
  }

  if (!items || items.length === 0) {
    return (
      <Card
        data-testid={dataTestId}
        sx={{
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        <CardContent>
          <Typography variant="body2" color="textSecondary" textAlign="center">
            {emptyMessage || translateText('dashboard.actionItems.empty')}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  // Group items by priority
  const highPriorityItems = items.filter((item) => item.priority === 'high');
  const mediumPriorityItems = items.filter((item) => item.priority === 'medium');
  const lowPriorityItems = items.filter((item) => item.priority === 'low');

  const handleItemClick = (item: any) => {
    if (item.navigationPath) {
      navigate(item.navigationPath);
    }
  };

  return (
    <Card
      data-testid={dataTestId}
      sx={{
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`,
        mb: 2,
      }}
    >
      <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          mb={2}
        >
          <Typography variant="h6" fontWeight={600}>
            {title}
          </Typography>
          <Chip
            label={items.length}
            size="small"
            color="primary"
            sx={{ fontWeight: 600 }}
          />
        </Box>

        {highPriorityItems.length > 0 && (
          <Box mb={2}>
            <Typography
              variant="caption"
              color="error"
              fontWeight={600}
              sx={{ mb: 1, display: 'block' }}
            >
              {translateText('dashboard.actionItems.highPriority')}
            </Typography>
            <List dense>
              {highPriorityItems.map((item) => (
                <ListItem
                  key={item.id}
                  button
                  onClick={() => handleItemClick(item)}
                  sx={{
                    borderRadius: 1,
                    mb: 0.5,
                    '&:hover': {
                      backgroundColor: theme.palette.error.light + '10',
                    },
                  }}
                  data-testid={`${dataTestId}-item-${item.id}`}
                >
                  <Box
                    sx={{
                      color: theme.palette.error.main,
                      mr: 1,
                      display: 'flex',
                      alignItems: 'center',
                      alignSelf: 'flex-start',
                    }}
                  >
                    {getPriorityIcon('high')}
                  </Box>
                  <ListItemText
                    primary={item.title}
                    secondary={item.description || item.count}
                    primaryTypographyProps={{
                      variant: 'body2',
                      fontWeight: 500,
                    }}
                    secondaryTypographyProps={{
                      variant: 'caption',
                    }}
                  />
                  {item.navigationPath && item.title && item.description && (
                    <ListItemSecondaryAction>
                      <Button
                        size="small"
                        endIcon={<ArrowForward />}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleItemClick(item);
                        }}
                        sx={{
                          minWidth: 40,
                          p: 0,
                        }}
                        data-testid={`${dataTestId}-item-${item.id}-cta`}
                      />
                    </ListItemSecondaryAction>
                  )}
                </ListItem>
              ))}
            </List>
          </Box>
        )}

        {mediumPriorityItems.length > 0 && (
          <Box mb={2}>
            <Typography
              variant="caption"
              color="warning.main"
              fontWeight={600}
              sx={{ mb: 1, display: 'block' }}
            >
              {translateText('dashboard.actionItems.mediumPriority')}
            </Typography>
            <List dense>
              {mediumPriorityItems.map((item) => (
                <ListItem
                  key={item.id}
                  button
                  onClick={() => handleItemClick(item)}
                  sx={{
                    borderRadius: 1,
                    mb: 0.5,
                    '&:hover': {
                      backgroundColor: theme.palette.warning.light + '10',
                    },
                  }}
                  data-testid={`${dataTestId}-item-${item.id}`}
                >
                  <Box
                    sx={{
                      color: theme.palette.warning.main,
                      mr: 1,
                      display: 'flex',
                      alignItems: 'center',
                      alignSelf: 'flex-start',
                    }}
                  >
                    {getPriorityIcon('medium')}
                  </Box>
                  <ListItemText
                    primary={item.title}
                    secondary={item.description || item.count}
                    primaryTypographyProps={{
                      variant: 'body2',
                      fontWeight: 500,
                    }}
                    secondaryTypographyProps={{
                      variant: 'caption',
                    }}
                  />
                  {item.navigationPath && item.title && item.description && (
                    <ListItemSecondaryAction>
                      <Button
                        size="small"
                        endIcon={<ArrowForward />}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleItemClick(item);
                        }}
                        sx={{
                          minWidth: 40,
                          p: 0,
                        }}
                        data-testid={`${dataTestId}-item-${item.id}-cta`}
                      />
                    </ListItemSecondaryAction>
                  )}
                </ListItem>
              ))}
            </List>
          </Box>
        )}

        {lowPriorityItems.length > 0 && (
          <Box>
            <Typography
              variant="caption"
              color="info.main"
              fontWeight={600}
              sx={{ mb: 1, display: 'block' }}
            >
              {translateText('dashboard.actionItems.lowPriority')}
            </Typography>
            <List dense>
              {lowPriorityItems.map((item) => (
                <ListItem
                  key={item.id}
                  button
                  onClick={() => handleItemClick(item)}
                  sx={{
                    borderRadius: 1,
                    mb: 0.5,
                    '&:hover': {
                      backgroundColor: theme.palette.info.light + '10',
                    },
                  }}
                  data-testid={`${dataTestId}-item-${item.id}`}
                >
                  <Box
                    sx={{
                      color: theme.palette.info.main,
                      mr: 1,
                      display: 'flex',
                      alignItems: 'center',
                      alignSelf: 'flex-start',
                    }}
                  >
                    {getPriorityIcon('low')}
                  </Box>
                  <ListItemText
                    primary={item.title}
                    secondary={item.description || item.count}
                    primaryTypographyProps={{
                      variant: 'body2',
                      fontWeight: 500,
                    }}
                    secondaryTypographyProps={{
                      variant: 'caption',
                    }}
                  />
                  {item.navigationPath && item.title && item.description && (
                    <ListItemSecondaryAction>
                      <Button
                        size="small"
                        endIcon={<ArrowForward />}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleItemClick(item);
                        }}
                        sx={{
                          minWidth: 40,
                          p: 0,
                        }}
                        data-testid={`${dataTestId}-item-${item.id}-cta`}
                      />
                    </ListItemSecondaryAction>
                  )}
                </ListItem>
              ))}
            </List>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};
